import {
  ExternalE2EEKeyProvider,
  Room,
  RoomEvent,
  Track,
  isE2EESupported,
} from 'livekit-client';
import {
  MESSENGER_V2_SFU_CAMERA_PUBLISH_OPTIONS,
  createMessengerV2InitialCallMedia,
  createMessengerV2NativeCameraTrack,
  resolveMessengerV2SfuRoomMediaOptions,
} from './sfuCallMediaPolicy.js';
import { createMessengerV2LiveKitE2eeWorker } from './sfuCallE2eeWorker.js';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const normalizeMode = (mode) => (mode === 'video' ? 'video' : 'voice');

export const isMessengerV2SfuCallE2eeSupported = () => {
  try {
    return typeof Worker !== 'undefined' && isE2EESupported();
  } catch {
    return false;
  }
};

export class MessengerV2SfuCallSession {
  constructor({
    callId,
    conversationId,
    livekitUrl,
    token,
    mode = 'voice',
    mediaKey = null,
    e2eeWorkerFactory = null,
    initialMediaFactory = null,
    cameraTrackFactory = createMessengerV2NativeCameraTrack,
    onConnected,
    onDisconnected,
    onParticipantConnected,
    onParticipantDisconnected,
    onParticipantUpdated,
    onTrackSubscribed,
    onTrackUnsubscribed,
    onLocalTrackPublished,
    onConnectionStateChanged,
    onActiveSpeakersChanged,
    onScreenShareStateChanged,
  } = {}) {
    this.callId = toStringOrNull(callId);
    this.conversationId = toStringOrNull(conversationId);
    this.livekitUrl = toStringOrNull(livekitUrl);
    this.token = toStringOrNull(token);
    this.mode = normalizeMode(mode);
    this.mediaKey = mediaKey;
    this.e2eeWorkerFactory =
      typeof e2eeWorkerFactory === 'function'
        ? e2eeWorkerFactory
        : createMessengerV2LiveKitE2eeWorker;
    this.initialMediaFactory =
      typeof initialMediaFactory === 'function'
        ? initialMediaFactory
        : createMessengerV2InitialCallMedia;
    this.cameraTrackFactory =
      typeof cameraTrackFactory === 'function'
        ? cameraTrackFactory
        : createMessengerV2NativeCameraTrack;
    this.cameraMedia = {
      stream: null,
      track: null,
      publication: null,
      publishPromise: null,
    };
    this.onConnected = typeof onConnected === 'function' ? onConnected : () => {};
    this.onDisconnected = typeof onDisconnected === 'function' ? onDisconnected : () => {};
    this.onParticipantConnected =
      typeof onParticipantConnected === 'function' ? onParticipantConnected : () => {};
    this.onParticipantDisconnected =
      typeof onParticipantDisconnected === 'function' ? onParticipantDisconnected : () => {};
    this.onParticipantUpdated =
      typeof onParticipantUpdated === 'function' ? onParticipantUpdated : () => {};
    this.onTrackSubscribed =
      typeof onTrackSubscribed === 'function' ? onTrackSubscribed : () => {};
    this.onTrackUnsubscribed =
      typeof onTrackUnsubscribed === 'function' ? onTrackUnsubscribed : () => {};
    this.onLocalTrackPublished =
      typeof onLocalTrackPublished === 'function' ? onLocalTrackPublished : () => {};
    this.onConnectionStateChanged =
      typeof onConnectionStateChanged === 'function' ? onConnectionStateChanged : () => {};
    this.onActiveSpeakersChanged =
      typeof onActiveSpeakersChanged === 'function' ? onActiveSpeakersChanged : () => {};
    this.room = null;
    this.keyProvider = null;
    this.e2eeWorker = null;
    this.disconnectPromise = null;
    this.stoppedMediaTracks = new WeakSet();
    this.screenShareOperation = null;
    this.onScreenShareStateChanged = onScreenShareStateChanged ?? (() => {});
  }

  async buildRoomOptions() {
    const options = resolveMessengerV2SfuRoomMediaOptions();

    if (!this.mediaKey) return options;
    if (!isE2EESupported()) {
      throw new Error('LiveKit E2EE is not supported by this browser');
    }
    if (!this.e2eeWorkerFactory) {
      throw new Error('LiveKit E2EE worker is required for encrypted SFU calls');
    }

    this.keyProvider = new ExternalE2EEKeyProvider();
    await this.keyProvider.setKey(this.mediaKey);
    this.e2eeWorker = this.e2eeWorkerFactory();
    if (!this.e2eeWorker) {
      throw new Error('LiveKit E2EE worker is required for encrypted SFU calls');
    }
    options.encryption = {
      keyProvider: this.keyProvider,
      worker: this.e2eeWorker,
    };
    return options;
  }

  bindRoomEvents(room) {
    room
      .on(RoomEvent.Connected, () => this.onConnected({ room }))
      .on(RoomEvent.Disconnected, (reason) => this.onDisconnected({ room, reason }))
      .on(RoomEvent.ConnectionStateChanged, (state) =>
        this.onConnectionStateChanged({ room, state }),
      )
      .on(RoomEvent.ParticipantConnected, (participant) =>
        this.onParticipantConnected({ room, participant }),
      )
      .on(RoomEvent.ParticipantDisconnected, (participant) =>
        this.onParticipantDisconnected({ room, participant }),
      )
      .on(RoomEvent.ParticipantNameChanged, (name, participant) =>
        this.onParticipantUpdated({ room, participant, name }),
      )
      .on(RoomEvent.ParticipantAttributesChanged, (attributes, participant) =>
        this.onParticipantUpdated({ room, participant, attributes }),
      )
      .on(RoomEvent.TrackPublished, (publication, participant) =>
        this.onParticipantUpdated({ room, participant, publication }),
      )
      .on(RoomEvent.TrackUnpublished, (publication, participant) =>
        this.onParticipantUpdated({ room, participant, publication }),
      )
      .on(RoomEvent.TrackMuted, (publication, participant) =>
        this.onParticipantUpdated({ room, participant, publication }),
      )
      .on(RoomEvent.TrackUnmuted, (publication, participant) =>
        this.onParticipantUpdated({ room, participant, publication }),
      )
      .on(RoomEvent.TrackSubscribed, (track, publication, participant) =>
        this.onTrackSubscribed({ room, track, publication, participant }),
      )
      .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) =>
        this.onTrackUnsubscribed({ room, track, publication, participant }),
      )
      .on(RoomEvent.LocalTrackPublished, (publication, participant) =>
        this.onLocalTrackPublished({ room, publication, participant }),
      )
      .on(RoomEvent.LocalTrackUnpublished, (publication, participant) =>
        this.onParticipantUpdated({ room, participant, publication }),
      );

    if (RoomEvent.TrackStreamStateChanged) {
      room.on(RoomEvent.TrackStreamStateChanged, (publication, streamState, participant) =>
        this.onParticipantUpdated({ room, participant, publication, streamState }),
      );
    }
    if (RoomEvent.ActiveSpeakersChanged) {
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) =>
        this.onActiveSpeakersChanged({ room, speakers }),
      );
    }
  }

  async createInitialLocalTracks() {
    const captured = await this.initialMediaFactory({ mode: this.mode });
    const tracks = Array.isArray(captured?.tracks)
      ? captured.tracks.filter(Boolean)
      : Array.from(captured?.stream?.getTracks?.() ?? []);
    const trackKinds = new Set(
      tracks.map((track) => track?.mediaStreamTrack?.kind ?? track?.kind ?? null),
    );
    if (!trackKinds.has('audio')) {
      tracks.forEach((track) => track?.stop?.());
      throw new Error('Microphone capture did not return an audio track');
    }
    if (this.mode === 'video' && !trackKinds.has('video')) {
      tracks.forEach((track) => track?.stop?.());
      throw new Error('Camera capture did not return a video track');
    }
    return tracks;
  }

  async publishInitialLocalTracks(room, tracks = []) {
    for (const track of tracks) {
      const kind = track?.mediaStreamTrack?.kind ?? track?.kind ?? null;
      const isVideo = kind === 'video';
      const publication = await room.localParticipant.publishTrack(track, {
        ...(isVideo ? MESSENGER_V2_SFU_CAMERA_PUBLISH_OPTIONS : {}),
        source: isVideo ? Track.Source.Camera : Track.Source.Microphone,
      });
      if (isVideo) {
        this.cameraMedia.track = track?.mediaStreamTrack ?? track;
        this.cameraMedia.publication = publication ?? null;
      }
    }
  }

  async connect() {
    if (!this.livekitUrl || !this.token) {
      throw new Error('LiveKit URL and token are required');
    }
    if (this.room) return this.room;

    const assertConnecting = () => {
      if (this.closed) throw new Error('Call connection was cancelled');
    };
    assertConnecting();
    const options = await this.buildRoomOptions();
    assertConnecting();
    const room = new Room(options);
    this.connectingRoom = room;
    this.bindRoomEvents(room);
    let initialTracks = [];
    try {
      initialTracks = await this.createInitialLocalTracks();
      assertConnecting();
      await room.connect(this.livekitUrl, this.token, { autoSubscribe: true });
      assertConnecting();
      this.room = room;
      if (this.mediaKey && typeof room.setE2EEEnabled === 'function') {
        await room.setE2EEEnabled(true);
      }
      await room.startAudio?.().catch(() => null);
      assertConnecting();
      await this.publishInitialLocalTracks(room, initialTracks);
      assertConnecting();
      return room;
    } catch (error) {
      initialTracks.forEach((track) => track?.stop?.());
      // The SDK may finish its connect after an earlier disconnect already resolved.
      if (this.closed) await room.disconnect();
      await this.disconnect();
      throw error;
    } finally {
      this.connectingRoom = null;
    }
  }

  collectLocalMediaStream() {
    const participant = this.room?.localParticipant;
    const publications =
      participant?.trackPublications?.values?.() ??
      participant?.tracks?.values?.() ??
      [];
    const tracks = Array.from(publications)
      .map((publication) => publication?.track?.mediaStreamTrack ?? publication?.track)
      .filter(Boolean);
    return tracks.length ? new MediaStream(tracks) : null;
  }

  startScreenShare() {
    if (this.screenShareOperation) return this.screenShareOperation.promise;
    const room = this.room;
    if (!room) return Promise.resolve(false);
    const operation = { room, tracks: [], cancelled: false, promise: null };
    this.screenShareOperation = operation;
    this.onScreenShareStateChanged({ status: 'selecting', error: null });
    // Invoke capture directly in the click's activation context, before any network await.
    operation.promise = (async () => {
      try {
        operation.tracks = await room.localParticipant.createScreenTracks({
          audio: true,
          contentHint: 'detail',
          resolution: { width: 1920, height: 1080, frameRate: 15 },
          selfBrowserSurface: 'exclude',
          // Tab audio is optional. Avoid feeding the call's own system output back to peers.
          systemAudio: 'exclude',
          surfaceSwitching: 'include',
        });
        if (operation.cancelled || this.room !== room) return false;
        if (!operation.tracks.some((track) => track.source === Track.Source.ScreenShare)) {
          throw new Error('Screen capture returned no video');
        }
        this.onScreenShareStateChanged({ status: 'publishing', error: null });
        operation.onEnded = () => {
          void this.stopScreenShare().catch(() => {
            this.onScreenShareStateChanged({ status: 'idle', error: 'publish_failed' });
          });
        };
        for (const track of operation.tracks) {
          track.mediaStreamTrack?.addEventListener?.('ended', operation.onEnded);
          await room.localParticipant.publishTrack(track, { source: track.source, simulcast: false });
          if (operation.cancelled || this.room !== room) return false;
        }
        this.onScreenShareStateChanged({ status: 'sharing', error: null });
        return true;
      } catch (error) {
        operation.cancelled = true;
        if (this.room === room) {
          this.onScreenShareStateChanged({
            status: 'idle',
            error: error?.name === 'NotAllowedError' ? 'permission_denied' : 'publish_failed',
          });
        }
        return false;
      } finally {
        if (operation.cancelled || this.room !== room) {
          await this.releaseScreenShare(operation);
          if (this.screenShareOperation === operation) this.screenShareOperation = null;
        }
      }
    })();
    return operation.promise;
  }

  async releaseScreenShare(operation) {
    if (operation.releasePromise) return operation.releasePromise;
    operation.releasePromise = (async () => {
      const results = await Promise.allSettled(operation.tracks.map(async (track) => {
        track.mediaStreamTrack?.removeEventListener?.('ended', operation.onEnded);
        // Stop capture immediately, even when the transport cannot acknowledge unpublish.
        track.stop?.();
        await operation.room.localParticipant.unpublishTrack(track);
      }));
      const released = results.every((result) => result.status === 'fulfilled');
      if (!released && this.room === operation.room) {
        this.onScreenShareStateChanged({ status: 'idle', error: 'publish_failed' });
      }
      return released;
    })();
    return operation.releasePromise;
  }

  async stopScreenShare() {
    const operation = this.screenShareOperation;
    if (!operation) return false;
    operation.cancelled = true;
    operation.tracks.forEach((track) => track.stop?.());
    this.onScreenShareStateChanged({ status: 'stopping', error: null });
    // A pending OS picker cannot be dismissed programmatically. Its continuation owns cleanup.
    await operation.promise;
    const released = await this.releaseScreenShare(operation);
    if (this.screenShareOperation === operation) this.screenShareOperation = null;
    this.onScreenShareStateChanged({ status: 'idle', error: released ? null : 'publish_failed' });
    return false;
  }

  async setMicrophoneEnabled(enabled) {
    if (!this.room) return false;
    const nextEnabled = enabled !== false;
    await this.room.localParticipant.setMicrophoneEnabled(nextEnabled);
    return nextEnabled;
  }

  async setSoundMuted(muted) {
    if (!this.room || this.closed) throw new Error('Call media connection is not active');
    await this.room.localParticipant.setAttributes({ sound_muted: String(muted === true) });
  }

  async setMuted(muted) {
    const nextMuted = muted === true;
    await this.setMicrophoneEnabled(!nextMuted);
    return nextMuted;
  }

  async listAudioOutputs() {
    const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!mediaDevices?.enumerateDevices) return [];

    const devices = await mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === 'audiooutput')
      .map((device, index) => ({
        deviceId: device.deviceId,
        groupId: device.groupId || null,
        label: device.label || `Audio output ${index + 1}`,
      }));
  }

  async setAudioOutput(deviceId) {
    const normalizedDeviceId = toStringOrNull(deviceId);
    if (!this.room || !normalizedDeviceId || typeof this.room.switchActiveDevice !== 'function') {
      return false;
    }

    const changed = await this.room.switchActiveDevice('audiooutput', normalizedDeviceId);
    return changed !== false;
  }

  async recoverMedia({ microphoneEnabled = true } = {}) {
    const room = this.room;
    if (!room) {
      return { recovered: false, audioOutputDevices: [] };
    }

    await room.startAudio?.().catch(() => null);

    const publication = room.localParticipant?.getTrackPublication?.(Track.Source.Microphone);
    const localTrack = publication?.track ?? null;
    const mediaTrack = localTrack?.mediaStreamTrack ?? localTrack;
    const microphoneNeedsRecovery =
      microphoneEnabled === true &&
      (!localTrack ||
        mediaTrack?.readyState === 'ended' ||
        mediaTrack?.muted === true ||
        publication.isMuted === true);
    if (microphoneNeedsRecovery) {
      if (
        localTrack &&
        (mediaTrack?.readyState === 'ended' || mediaTrack?.muted === true) &&
        typeof localTrack.restartTrack === 'function'
      ) {
        await localTrack.restartTrack();
      } else {
        await room.localParticipant.setMicrophoneEnabled(true);
      }
    }

    const audioOutputDevices = await this.listAudioOutputs().catch(() => []);
    return {
      recovered: true,
      microphoneRecovered: microphoneNeedsRecovery,
      audioOutputDevices,
      audioOutputDeviceId: room.getActiveDevice?.('audiooutput') ?? null,
    };
  }

  resolveLocalCameraPublication() {
    return (
      this.room?.localParticipant?.getTrackPublication?.(Track.Source.Camera) ??
      this.cameraMedia.publication ??
      null
    );
  }

  async publishNativeCameraTrack() {
    if (!this.room) return false;
    if (this.cameraMedia.publishPromise) return this.cameraMedia.publishPromise;

    const room = this.room;
    const publishPromise = (async () => {
      const captured = await this.cameraTrackFactory();
      const stream = captured?.stream ?? null;
      const track = captured?.track ?? stream?.getVideoTracks?.()[0] ?? null;
      if (!track) {
        stream?.getTracks?.().forEach((mediaTrack) => mediaTrack?.stop?.());
        throw new Error('Camera capture did not return a video track');
      }
      if (this.room !== room) {
        stream?.getTracks?.().forEach((mediaTrack) => mediaTrack?.stop?.());
        track?.stop?.();
        return false;
      }

      try {
        const publication = await room.localParticipant.publishTrack(track, {
          ...MESSENGER_V2_SFU_CAMERA_PUBLISH_OPTIONS,
          source: Track.Source.Camera,
        });
        this.cameraMedia.stream = stream;
        this.cameraMedia.track = track;
        this.cameraMedia.publication = publication ?? null;
        return true;
      } catch (error) {
        stream?.getTracks?.().forEach((mediaTrack) => mediaTrack?.stop?.());
        track?.stop?.();
        throw error;
      }
    })();

    this.cameraMedia.publishPromise = publishPromise;
    try {
      return await publishPromise;
    } finally {
      if (this.cameraMedia.publishPromise === publishPromise) {
        this.cameraMedia.publishPromise = null;
      }
    }
  }

  async setCameraEnabled(enabled) {
    if (!this.room) return false;
    const nextEnabled = enabled !== false;
    if (!nextEnabled && this.cameraMedia.publishPromise) {
      await this.cameraMedia.publishPromise.catch(() => false);
    }

    const publication = this.resolveLocalCameraPublication();
    const mediaTrack =
      publication?.track?.mediaStreamTrack ?? this.cameraMedia.track ?? null;
    const hasLivePublication =
      Boolean(publication?.track) && mediaTrack?.readyState !== 'ended';

    if (hasLivePublication) {
      if (nextEnabled) {
        await publication.unmute?.();
      } else {
        await publication.mute?.();
      }
      return nextEnabled;
    }

    if (!nextEnabled) return false;
    return this.publishNativeCameraTrack();
  }

  stopCameraMedia() {
    const tracks = new Set([
      ...(this.cameraMedia.stream?.getTracks?.() ?? []),
      this.cameraMedia.track,
    ]);
    tracks.forEach((track) => track?.stop?.());
    this.cameraMedia.stream = null;
    this.cameraMedia.track = null;
    this.cameraMedia.publication = null;
    this.cameraMedia.publishPromise = null;
  }

  stopAllLocalMedia(room = null) {
    const mediaTracks = new Set([
      ...(this.cameraMedia.stream?.getTracks?.() ?? []),
      this.cameraMedia.track,
    ]);
    const publications = room?.localParticipant?.trackPublications;
    const publicationList =
      typeof publications?.values === 'function'
        ? Array.from(publications.values())
        : Array.isArray(publications)
          ? publications
          : [];
    publicationList.forEach((publication) => {
      const localTrack = publication?.track;
      mediaTracks.add(localTrack?.mediaStreamTrack ?? localTrack ?? null);
    });
    mediaTracks.forEach((track) => {
      if (!track || (typeof track !== 'object' && typeof track !== 'function')) return;
      if (this.stoppedMediaTracks.has(track)) return;
      this.stoppedMediaTracks.add(track);
      track.stop?.();
    });
    this.cameraMedia.stream = null;
    this.cameraMedia.track = null;
    this.cameraMedia.publication = null;
    this.cameraMedia.publishPromise = null;
  }

  async disconnect() {
    this.closed = true;
    if (this.disconnectPromise) return this.disconnectPromise;
    const room = this.room || this.connectingRoom;
    this.room = null;
    const screenShare = this.screenShareOperation;
    if (screenShare) {
      screenShare.cancelled = true;
      screenShare.tracks.forEach((track) => track.stop?.());
      // Do not hold hangup hostage to a pending browser permission dialog.
      void screenShare.promise?.then(async () => {
        await this.releaseScreenShare(screenShare);
        if (this.screenShareOperation === screenShare) this.screenShareOperation = null;
      });
    }
    const pendingCameraPublish = this.cameraMedia.publishPromise;
    this.stopAllLocalMedia(room);
    const disconnectPromise = (async () => {
      try {
        if (pendingCameraPublish) {
          await pendingCameraPublish.catch(() => false);
        }
        if (room) {
          await room.disconnect();
        }
      } finally {
        this.stopAllLocalMedia(room);
        try {
          this.e2eeWorker?.terminate?.();
        } catch {}
        this.e2eeWorker = null;
        this.keyProvider = null;
      }
    })();
    this.disconnectPromise = disconnectPromise;
    return disconnectPromise;
  }

  close() {
    return this.disconnect();
  }
}

export const attachMessengerV2SfuTrack = (track, element) => {
  if (!track || !element || typeof track.attach !== 'function') return null;
  return track.attach(element);
};

export const detachMessengerV2SfuTrack = (track, element = null) => {
  if (!track || typeof track.detach !== 'function') return [];
  return element ? track.detach(element) : track.detach();
};

export const resolveMessengerV2SfuTrackKind = (track = null) => {
  const source = toStringOrNull(track?.source ?? null);
  if (source === Track.Source.Camera) return 'video';
  if (source === Track.Source.Microphone) return 'audio';
  return toStringOrNull(track?.kind ?? null) || null;
};

export default MessengerV2SfuCallSession;
